/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { usePanelActionsInternal } from '../../panel-manager/panel-provider';
import { usePanelStateInternal } from '../../panel-manager/use-panel-state-internal';
import { PanelRouter } from '../../panels-renderer/panel-router';
import { PanelsRenderer } from '../../panels-renderer/panels-renderer';

import { PanelSlot } from './panel-slot';

const styles = cssMap({
	layout: {
		display: 'grid',
		gridTemplateAreas: `'main'`,
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: '1fr',
		height: '100%',
		width: '100%',
		// Panel is only shown as a separate column on large viewports
		'@media (min-width: 90rem)': {
			gridTemplateAreas: `
                "main panel"
           `,
			gridTemplateRows: 'auto',
			gridTemplateColumns: 'minmax(0,1fr) auto',
		},
		overflow: 'auto',
	},
	mainContent: {
		gridArea: 'main',
		overflow: 'auto',
		display: 'flex',

		height: '100%',
	},
});

export type LayoutWithPanelProps = {
	/**
	 * Panel width. Defaults to 365px.
	 * Can be overridden via resizing.
	 */
	panelWidth?: number;
	testId?: string;
	children: ReactNode;
	/**
	 * Called when resizing is complete.
	 * @param finalWidth number representing the new width of the panel in px
	 *
	 */
	onResizeEnd?: (finalWidth: number) => void;
};

export function LayoutWithPanel({
	children,
	panelWidth,
	testId,
	onResizeEnd,
}: LayoutWithPanelProps): JSX.Element {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();

	const [persistedPanelWidth, setPersistedPanelWidth] = useState(panelWidth ?? state.panelWidth);
	const panelSlotTestId = testId && `${testId}--panel-slot`;

	const activePanelRef = useRef(state.activePanels[0]);
	const managerRef = useRef(manager);

	// Keep refs up to date on every render so the cleanup always sees the latest values
	activePanelRef.current = state.activePanels[0];
	managerRef.current = manager;

	useEffect(() => {
		/**
		 * Close the active panel when this component unmounts.
		 * Ensures panels do not persist when navigating to a new page.
		 */
		return () => {
			if (activePanelRef.current) {
				managerRef.current.closePanel(activePanelRef.current.instanceId);
			}
		};
	}, []);

	return (
		<Box xcss={styles.layout} testId={testId}>
			<Box xcss={styles.mainContent}>{children}</Box>

			<PanelsRenderer>
				<PanelRouter
					wrapper={({ children }) => (
						<PanelSlot
							children={children}
							defaultWidth={persistedPanelWidth}
							onResizeEnd={(finalWidth) => {
								setPersistedPanelWidth(finalWidth);
								manager.setPanelWidth(finalWidth);
								onResizeEnd?.(finalWidth);
							}}
							testId={panelSlotTestId}
						/>
					)}
				/>
			</PanelsRenderer>
		</Box>
	);
}
